// src/components/diagrams/RegularPolygonInscribedAngleDiagram.js
import React from "react";
import { InlineMath } from "react-katex";

/**
 * Diagram: okrąg + foremny n-kąt + kąt wpisany przy jednym wierzchołku.
 *
 * - n: liczba boków (5–12)
 * - iCenter: index wierzchołka, w którym jest kąt wpisany
 * - iLeft, iRight: indeksy końców łuku (kolejne wierzchołki na okręgu)
 *
 * Wierzchołki są podpisane literami A, B, C, ...
 * Sam kąt jest wypełniony półprzezroczystym fioletem.
 */
export default function RegularPolygonInscribedAngleDiagram({
  n,
  iCenter,
  iLeft,
  iRight,
  size = 280,
  circleR = 110,
}) {
  const N = Math.max(3, Math.min(20, n || 6));
  const cx = size / 2;
  const cy = size / 2;

  const COLORS = {
    accent: "#f59e0b",
    polygon: "currentColor",
    angleFill: "rgba(245, 158, 11, 0.2)",
    angleStroke: "#f59e0b",
  };

  const letters = "ABCDEFGHIJKL".split("");

  const angleStep = (2 * Math.PI) / N;
  // zaczynamy mniej więcej na dole, żeby A było "na dole po lewej" jak w maturze
  const startAngle = Math.PI * 0.7;

  const verts = [];
  for (let i = 0; i < N; i++) {
    const ang = startAngle + i * angleStep;
    verts.push({
      x: cx + circleR * Math.cos(ang),
      y: cy + circleR * Math.sin(ang),
      ang,
    });
  }

  const idxCenter = ((iCenter % N) + N) % N;
  const idxLeft = ((iLeft % N) + N) % N;
  const idxRight = ((iRight % N) + N) % N;

  const V = verts[idxCenter];
  const A = verts[idxLeft];
  const B = verts[idxRight];

  // polygon path
  const polyPath =
    "M " +
    verts
      .map((p) => `${p.x} ${p.y}`)
      .join(" L ") +
    " Z";

  // łuk wierzchołkowy kąta wpisanego (mały)
  const arcR = 26;
  const a1 = Math.atan2(A.y - V.y, A.x - V.x);
  const a2 = Math.atan2(B.y - V.y, B.x - V.x);

  let d = a2 - a1;
  while (d <= -Math.PI) d += 2 * Math.PI;
  while (d > Math.PI) d -= 2 * Math.PI;
  const sweepFlag = d >= 0 ? 1 : 0;
  const largeFlag = Math.abs(d) > Math.PI ? 1 : 0;

  const arcStart = {
    x: V.x + arcR * Math.cos(a1),
    y: V.y + arcR * Math.sin(a1),
  };
  const arcEnd = {
    x: V.x + arcR * Math.cos(a2),
    y: V.y + arcR * Math.sin(a2),
  };

  const anglePath = `
    M ${V.x} ${V.y}
    L ${arcStart.x} ${arcStart.y}
    A ${arcR} ${arcR} 0 ${largeFlag} ${sweepFlag} ${arcEnd.x} ${arcEnd.y}
    Z
  `;

  // pomocnicza pozycja dla etykiet (trochę na zewnątrz)
  const placeOutside = (px, py, offset = 16) => {
    const vx = px - cx;
    const vy = py - cy;
    const len = Math.hypot(vx, vy) || 1;
    const ux = vx / len;
    const uy = vy / len;
    const x = px + offset * ux;
    const y = py + offset * uy;
    let anchor = "middle";
    if (ux > 0.35) anchor = "start";
    else if (ux < -0.35) anchor = "end";
    return { x, y, anchor };
  };

  const LatexLabel = ({
    x,
    y,
    math,
    color = "currentColor",
    width = 42,
    height = 24,
    anchor = "middle",
    fontSize = 15,
  }) => {
    const boxX = anchor === "start" ? x : anchor === "end" ? x - width : x - width / 2;
    const textAlign = anchor === "start" ? "left" : anchor === "end" ? "right" : "center";

    return (
      <foreignObject x={boxX} y={y - height / 2} width={width} height={height}>
        <div style={{ color, fontSize, lineHeight: 1.15, textAlign }}>
          <InlineMath math={String(math)} />
        </div>
      </foreignObject>
    );
  };

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ maxWidth: "100%", height: "auto" }}>
      {/* okrąg */}
      <circle
        cx={cx}
        cy={cy}
        r={circleR}
        fill="none"
        stroke={COLORS.polygon}
        strokeWidth="2"
      />

      {/* wielokąt foremny */}
      <path
        d={polyPath}
        fill="none"
        stroke={COLORS.polygon}
        strokeWidth="2.2"
      />

      {/* cięciwy kąta wpisanego */}
      <line
        x1={V.x}
        y1={V.y}
        x2={A.x}
        y2={A.y}
        stroke={COLORS.accent}
        strokeWidth="2.75"
      />
      <line
        x1={V.x}
        y1={V.y}
        x2={B.x}
        y2={B.y}
        stroke={COLORS.accent}
        strokeWidth="2.75"
      />

      {/* wypełnienie kąta */}
      <path
        d={anglePath}
        fill={COLORS.angleFill}
        stroke={COLORS.angleStroke}
        strokeWidth="2"
      />

      {/* punkty */}
      {verts.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="3.4" fill="currentColor" />
      ))}

      {/* etykiety wierzchołków */}
      {verts.map((p, i) => {
        const pos = placeOutside(p.x, p.y);
        const label = letters[i] || `V${i + 1}`;
        return (
          <LatexLabel
            key={`lab-${i}`}
            x={pos.x}
            y={pos.y}
            math={label}
            anchor={pos.anchor}
            color={COLORS.polygon}
            fontSize={16}
          />
        );
      })}

      {/* środek S */}
      <circle cx={cx} cy={cy} r="3" fill="currentColor" />
      <LatexLabel
        x={cx}
        y={cy - 10}
        math="S"
        color={COLORS.polygon}
        fontSize={16}
      />
    </svg>
  );
}
